import { ICommand, RelayCommand } from "Application/Commands";
import { StudentSessionReportManagersConfig } from "../Interfaces/IStudentSessionReportViewModel";
import { KeyValuePair, FieldType } from "@shoothill/core";
import { SessionDocument } from "Shared/Models/StudentSessionReport";
import moment from "moment";

/**
 * Manages all commands for SessionReportViewModel.
 * Responsibilities: UI command definitions, event handlers, field updates.
 */
export class CommandsManager {
    // Navigation Commands
    public navigateToBackCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.mobx.setCancelUpdateWarningMessage("Are you sure you want to navigate back? Any changes you made will be lost.");
    });

    public confirmNavigateToStudentSessionsListCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.mobx.setCancelUpdateWarningMessage(null);
        this.viewModel.history.push(this.config.getNavigationConfirmPath(this.viewModel));
    });

    public dismissCancelUpdateWarningCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.mobx.setCancelUpdateWarningMessage(null);
    });

    public dismissFormHasErrorsWarningCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.mobx.setFormErrorMessage(null);
    });

    public cancelCommand: ICommand = new RelayCommand(() => {
        this.viewModel.history.push(this.config.getCancelPath(this.viewModel));
    });

    // Session Field Update Commands
    public updateSessionDateCommand: ICommand = new RelayCommand((value: Date) => {
        this.updateField("sessionDate", value);
    });

    public updateSessionStartTimeCommand: ICommand = new RelayCommand((value: string) => {
        const sessionStartTime = moment(new Date(value));
        this.updateField("sessionStartTime", sessionStartTime);
    });

    public updateSessionEndTimeCommand: ICommand = new RelayCommand((value: string) => {
        const sessionEndTime = moment(new Date(value));
        this.updateField("sessionEndTime", sessionEndTime);
    });

    public updateSessionStatusCommand: ICommand = new RelayCommand((kvp: KeyValuePair) => {
        this.updateField("attendance", kvp.key);
        if (kvp.key === "0") {
            // Absent selected
            this.updateField("activityTypeId", null);
            this.updateField("wellbeingId", null);
            this.updateField("involvementId", null);
            this.updateField("sessionPlan", "");
            this.updateField("subTargets", "");
            this.updateField("mentoring", "");
            this.viewModel.studentAbsenceViewModel.updateIsAttendedCommand.execute(false);
        } else {
            // Attended selected
            this.viewModel.studentAbsenceViewModel.reUpdateAbsenseTypeIdCommand.execute(null);
            this.viewModel.studentAbsenceViewModel.updateReasonCommand.execute("");
            this.viewModel.studentAbsenceViewModel.updateIsAttendedCommand.execute(true);
            this.viewModel.managers.mobx.setShowOtherAbsenceText(false);
        }
    });

    // Absence Commands
    public updateAbsenceReasonCommand: ICommand = new RelayCommand((kvp: KeyValuePair) => {
        this.viewModel.studentAbsenceViewModel.updateAbsenseTypeIdCommand.execute(kvp);
        const isOther = kvp.text === "Other";
        this.viewModel.managers.mobx.setShowOtherAbsenceText(isOther);
        if (!isOther) {
            this.viewModel.studentAbsenceViewModel.updateReasonCommand.execute("");
        }
    });

    public updateAbsenceReasonDetailCommand: ICommand = new RelayCommand((value: string) => {
        this.viewModel.studentAbsenceViewModel.updateReasonCommand.execute(value);
    });

    // Activity & Engagement Commands
    public updateActivityTypeCommand: ICommand = new RelayCommand((kvp: KeyValuePair) => {
        this.updateField("activityTypeId", kvp.key);
        if (kvp) {
            this.updateField("activityOptionName", kvp);
        } else {
            this.updateField("activityOptionName", null);
        }

        if (!kvp.key) {
            this.viewModel.managers.api.saveActivityOptions();
        }
    });

    public updateWellbeingCommand: ICommand = new RelayCommand((value: string) => {
        this.viewModel.setValue("wellbeingId", value);
    });

    public updateInvolvementCommand: ICommand = new RelayCommand((value: string) => {
        this.viewModel.setValue("involvementId", value);
    });

    // Target Selection Command
    public checkSelectStudentTargetCommand: ICommand = new RelayCommand((id: string) => {
        if (id) {
            const index = this.viewModel.model.studentTargetIds.indexOf(id);
            if (index === -1) {
                this.viewModel.model.studentTargetIds.push(id);
            } else {
                this.viewModel.model.studentTargetIds.splice(index, 1);
            }
        }
    });

    // Document & Filing Commands
    public updateDocumentCategoryCommand: ICommand = new RelayCommand((kvp: KeyValuePair) => {
        this.updateField("documentCategoryId", kvp.key);
    });

    public updateReportIncidentCommand: ICommand = new RelayCommand((value: boolean) => {
        this.updateField("reportIncident", value);
    });

    // Session Notes Commands
    public updateSessionPlanCommand: ICommand = new RelayCommand((value: string) => {
        this.updateField("sessionPlan", value);
    });

    public updateSessionFeedbackCommand: ICommand = new RelayCommand((value: string) => {
        this.updateField("sessionFeedback", value);
    });

    // Mentor Commands
    public updateMentorCommand: ICommand = new RelayCommand((value: KeyValuePair) => {
        this.updateField("mentorId", value.key);
    });

    // File Upload Commands
    public uploadStudentReportCommand: ICommand = new RelayCommand(async () => {
        await this.viewModel.managers.api.uploadSessionDocuments();
    });

    public removeStudentDocumentsCommand: ICommand = new RelayCommand((studentDocument: SessionDocument) => {
        studentDocument.setValue("isDeleted", true);
    });

    public downloadResourceDocumentCommand: ICommand = new RelayCommand(async (id: string) => {
        await this.viewModel.managers.api.downloadDocument(id);
    });

    public showSessionDocPhotoCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.mobx.toggleShowSessionDocPhoto();
    });

    // Submit & Export Commands
    public upsertStudentSessionCommand: ICommand = new RelayCommand(
        async () => {
            await this.viewModel.managers.api.upsertStudentSession();
        },
        () => !this.viewModel.managers.mobx.isUpserting,
    );

    public exportReportCommand: ICommand = new RelayCommand(async () => {
        await this.viewModel.managers.api.exportReport();
    });

    // Management Commands
    public resetApiClientErrorCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.api.reset();
    });

    constructor(
        private viewModel:
            | import("../StudentSessionReportViewModel").StudentSessionReportViewModel
            | import("Views/Mentors/Mentor/WeeklyRota/Form/WeeklyRotaSessionReportViewModel").WeeklyRotaSessionReportViewModel,
        private config: StudentSessionReportManagersConfig,
    ) {}

    /**
     * Private helper to update a field with validation.
     */
    private updateField = (fieldName: keyof FieldType<any>, value: any) => {
        this.viewModel.setValue(fieldName as any, value);
        this.viewModel.isFieldValid(fieldName as any);
    };
}
